import ArchiveIcon from '@mui/icons-material/Archive';
import RestoreIcon from '@mui/icons-material/Restore';
import MoreVert from '@mui/icons-material/MoreVert';
import { Box, Popover, Stack } from '@mui/material';
import { WithInputForm } from 'components/dataCreationForms/withInputForm';
import { MemoInspectionForm } from 'components/inspection/Form';
import { ButtonIcon, ButtonTextIcon, actionToolbarSx } from 'components/general';
import { MemoForbiddenTooltip } from 'components/general/ForbiddenTooltip/ForbiddenTooltip';
import { InspectionPdfButton } from 'components/reports/inspection/InspectionPdfButton';
import { InspectionTemplateItemModel } from 'components/dataProviders/withRequiredInspectionTemplate/types';
import { formatTranslation } from 'intl';
import { CommonTranslationKeys } from 'intl/keys';
import useIsMobile from 'hooks/useIsMobile';
import { usePopover } from 'hooks/usePopover';
import { usePermissions } from 'hooks/permissions/usePermissions';
import { ReactElement, useCallback, useEffect, useMemo } from 'react';
import { useLatestRef } from 'hooks/useLatestRef';
import { useResettableState } from 'hooks/useResettableState';
import {
  useAppUrlParams,
  UrlParamName,
} from 'hooks/navigation/useAppUrlParams';
import { useIntl } from 'react-intl';
import { InspectionStatus } from 'shared/domain/inspection/inspectionModel';
import { Permission } from 'shared/domain/permissions/types';
import {
  useInspection,
  useInspectionActions,
  useAtomicPersist,
  useInspectionStore,
  useInspectionTemplateOnView,
} from 'store/inspection';
import {
  createInspectionFormValidation,
  inspectionValidation,
  validateInspectionField,
} from 'views/inspections/wizard/validation';
import {
  ATOMIC_INSPECTION_FORM_EVENT_TYPE,
  inspectionToFlatForm,
} from 'views/inspections/wizard/model';
import { SingleInspectionSkeleton } from 'views/inspections/single/skeleton';

// Atomic mode persists per field; the form-level submit is intentionally inert.
const submitNoop = (): Promise<void> => Promise.resolve();
const dispatchSubmitNoop = (): void => {};

function InspectionAtomicFormContent({
  editPermission,
  actionButtons,
  inspectionStatus,
  onStatusChange,
}: {
  editPermission: Permission;
  actionButtons: ReactElement;
  inspectionStatus: InspectionStatus;
  onStatusChange: (status: InspectionStatus) => void;
}): ReactElement {
  return (
    <MemoInspectionForm
      loading={false}
      atomicMode
      editPermission={editPermission}
      actionButtons={actionButtons}
      inspectionStatus={inspectionStatus}
      onStatusChange={onStatusChange}
    />
  );
}

function InspectionViewForm({
  inspectionId,
  initialValues,
  editPermission,
  actionButtons,
  inspectionStatus,
  onStatusChange,
  template,
}: {
  inspectionId: string;
  initialValues: ReturnType<typeof inspectionToFlatForm>;
  editPermission: Permission;
  actionButtons: ReactElement;
  inspectionStatus: InspectionStatus;
  onStatusChange: (status: InspectionStatus) => void;
  template: InspectionTemplateItemModel;
}): ReactElement {
  const setAtomicSaveEnabled = useInspectionStore.use.setAtomicSaveEnabled();

  useEffect(() => {
    setAtomicSaveEnabled(true);
    return () => setAtomicSaveEnabled(false);
  }, [setAtomicSaveEnabled]);

  const getProtocolItemsForFinish = useCallback(
    () => undefined,
    []
  );

  const handleFormValidation = useMemo(
    () =>
      createInspectionFormValidation(
        inspectionValidation,
        template,
        getProtocolItemsForFinish
      ),
    [template, getProtocolItemsForFinish]
  );

  return (
    <WithInputForm
      key={inspectionId}
      values={initialValues}
      errors={{}}
      eventType={ATOMIC_INSPECTION_FORM_EVENT_TYPE}
      handleFieldValidation={validateInspectionField}
      handleFormValidation={handleFormValidation}
      onSubmit={submitNoop}
      dispatchSubmit={dispatchSubmitNoop}
      formName='inspection'
      enableAtomicSave
    >
      <InspectionAtomicFormContent
        editPermission={editPermission}
        actionButtons={actionButtons}
        inspectionStatus={inspectionStatus}
        onStatusChange={onStatusChange}
      />
    </WithInputForm>
  );
}

function InspectionViewFormWithStatus({
  inspectionId,
  initialValues,
  editPermission,
  actionButtons,
  initialStatus,
  template,
}: {
  inspectionId: string;
  initialValues: ReturnType<typeof inspectionToFlatForm>;
  editPermission: Permission;
  actionButtons: ReactElement;
  initialStatus: InspectionStatus;
  template: InspectionTemplateItemModel;
}): ReactElement {
  // The status the user just chose, shown until the inspection's status changes in the store.
  const [pendingStatus, setPendingStatus] = useResettableState<InspectionStatus | undefined>(
    initialStatus,
    () => undefined
  );
  const inspectionStatus = pendingStatus ?? initialStatus;
  const inspectionStatusRef = useLatestRef(inspectionStatus);
  const { saveInspectionStatus } = useAtomicPersist();

  const handleStatusChange = useCallback(
    (status: InspectionStatus) => {
      const previousStatus = inspectionStatusRef.current;
      setPendingStatus(status);
      // The backend can silently keep the previous value (e.g. a business
      // rule rejects the transition) even on a 200 response - roll the
      // optimistic UI back instead of leaving it out of sync until reload.
      // 'skipped' (atomic save disabled, superseded by a newer save, etc.) is
      // a legitimate no-op, not a failure - only 'failed' warrants a revert.
      void saveInspectionStatus(status === InspectionStatus.completed).then(
        (outcome) => {
          if (outcome !== 'failed') {
            return;
          }
          // Only revert if nothing newer has changed the status since we set
          // it (e.g. this call was aborted by a subsequent status change) -
          // otherwise we'd clobber that newer optimistic update.
          setPendingStatus((current) =>
            current === status ? previousStatus : current
          );
        }
      );
    },
    [saveInspectionStatus, inspectionStatusRef, setPendingStatus]
  );

  return (
    <InspectionViewForm
      inspectionId={inspectionId}
      initialValues={initialValues}
      editPermission={editPermission}
      actionButtons={actionButtons}
      inspectionStatus={inspectionStatus}
      onStatusChange={handleStatusChange}
      template={template}
    />
  );
}

export function InspectionViewContainer(): ReactElement {
  const intl = useIntl();
  const isMobile = useIsMobile();
  const documentId = useAppUrlParams(UrlParamName.DOCUMENT_ID);
  const { anchorEl, popoverOpen, attachPopover, closePopover } = usePopover();
  const { canEditInspection, canDeleteInspection } = usePermissions();
  const { template } = useInspectionTemplateOnView();
  const { inspection, inspectionDocuments, inspectionDocumentsLoading } =
    useInspection();

  const { archiveInspection, restoreInspection } = useInspectionActions();

  const isArchived = Boolean(inspection?.deleted);
  const deletePermission = canDeleteInspection(isArchived);
  const editPermission = canEditInspection(
    Boolean(inspection?.isCompleted),
    isArchived
  );

  const handleArchive = useCallback(() => {
    if (!inspection?._id) {
      return;
    }

    archiveInspection(inspection._id).catch(() => {});
  }, [archiveInspection, inspection?._id]);

  const handleRestore = useCallback(() => {
    if (!inspection?._id) {
      return;
    }

    restoreInspection(inspection._id).catch(() => {});
  }, [restoreInspection, inspection?._id]);

  const actionButtons = useMemo(
    () => (
      <>
        {isArchived ? (
          <MemoForbiddenTooltip
            visibleWhen={!deletePermission.permission}
            reason={deletePermission.reason}
            inlined
          >
            <ButtonTextIcon
              disabled={!deletePermission.permission}
              startIcon={RestoreIcon}
              onClick={handleRestore}
            >
              {formatTranslation(intl, CommonTranslationKeys.restore)}
            </ButtonTextIcon>
          </MemoForbiddenTooltip>
        ) : (
          <MemoForbiddenTooltip
            visibleWhen={!deletePermission.permission}
            reason={deletePermission.reason}
            inlined
          >
            <ButtonTextIcon
              disabled={!deletePermission.permission}
              startIcon={ArchiveIcon}
              onClick={handleArchive}
            >
              {formatTranslation(intl, CommonTranslationKeys.archive)}
            </ButtonTextIcon>
          </MemoForbiddenTooltip>
        )}
        <InspectionPdfButton />
      </>
    ),
    [
      deletePermission.permission,
      deletePermission.reason,
      handleArchive,
      handleRestore,
      intl,
      isArchived,
    ]
  );

  const mobileActionButtons = useMemo(
    () => (
      <>
        <ButtonIcon
          onClick={attachPopover}
          size='small'
          sx={{
            color: 'action.active',
            '&:hover': {
              backgroundColor: 'action.hover',
            },
          }}
        >
          <MoreVert />
        </ButtonIcon>
        <Popover
          open={popoverOpen}
          anchorEl={anchorEl}
          onClose={closePopover}
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
        >
          <Stack
            direction='column'
            sx={{
              p: 1,
              '& button': {
                width: '100%',
                justifyContent: 'flex-start',
                px: 2.5,
              },
            }}
          >
            {actionButtons}
          </Stack>
        </Popover>
      </>
    ),
    [actionButtons, anchorEl, attachPopover, closePopover, popoverOpen]
  );

  const desktopActionButtons = useMemo(
    () => <Box sx={actionToolbarSx}>{actionButtons}</Box>,
    [actionButtons]
  );

  const initialValues = useMemo(
    () => inspectionToFlatForm(inspection, inspectionDocuments),
    [inspection, inspectionDocuments]
  );

  const shouldWaitForDocuments = Boolean(documentId) && inspectionDocumentsLoading;

  if (!inspection?._id || !template || shouldWaitForDocuments) {
    return <SingleInspectionSkeleton />;
  }

  return (
    <InspectionViewFormWithStatus
      key={inspection._id}
      inspectionId={inspection._id}
      initialValues={initialValues}
      editPermission={editPermission}
      actionButtons={isMobile ? mobileActionButtons : desktopActionButtons}
      initialStatus={inspection.status}
      template={template}
    />
  );
}
